import { Modal, Form, Select, DatePicker, message, Alert, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import type { AppUser } from '../../../types';

interface BulkAssignAuditorModalProps {
  visible: boolean;
  onClose: () => void;
  /** IDs de las auditorías seleccionadas a asignar en bloque. */
  auditIds: number[];
  /** Auditores ya cargados en la vista padre (opcional; el modal
   *  hace fetch fresco filtrado por audit_company + country). */
  auditors: AppUser[];
  /** Filtros para /app-users/ (mismo criterio que AssignAuditorModal
   *  individual: solo auditores del audit_company + country de esta
   *  planning). */
  auditCompanyId?: number | null;
  countryId?: number | null;
  onSuccess?: () => void;
}

/**
 * Asignación masiva de auditor + fecha planificada a N auditorías.
 *
 * Copia el flujo de `AssignAuditorModal` pero aplicado a un array de
 * IDs. Cada auditoría se actualiza con un PATCH individual (el mismo
 * endpoint /audits/{id}/ que la versión single). Usamos
 * `Promise.allSettled` para tolerar fallos parciales — al terminar se
 * muestra un resumen con éxitos y fallos.
 *
 * Los campos son opcionales: dejar auditor vacío + fecha vacía cierra
 * el modal sin PATCH. Auditor "Sin asignar" (sentinel 0) sí ejecuta
 * el PATCH con `auditor_user: null` para desasignar en masa.
 */
export const BulkAssignAuditorModal: React.FC<BulkAssignAuditorModalProps> = ({
  visible,
  onClose,
  auditIds,
  auditors,
  auditCompanyId,
  countryId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loadedAuditors, setLoadedAuditors] = useState<AppUser[] | null>(null);
  const [loadingAuditors, setLoadingAuditors] = useState(false);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      setLoadingAuditors(true);
      try {
        const { apiClient } = await import('../../../services/api/apiClient');
        const params = new URLSearchParams({ page_size: '500' });
        if (auditCompanyId != null) params.set('audit_company', String(auditCompanyId));
        if (countryId != null) params.set('country', String(countryId));
        const response = await apiClient.get(`/app-users/?${params.toString()}`);
        const list =
          response.data?.results ?? response.data?.data ?? response.data ?? [];
        if (!cancelled) setLoadedAuditors(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setLoadedAuditors([]);
      } finally {
        if (!cancelled) setLoadingAuditors(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, auditCompanyId, countryId]);

  const auditorOptions = (() => {
    const map = new Map<number, AppUser>();
    for (const a of auditors || []) map.set(a.id, a);
    for (const a of loadedAuditors || []) map.set(a.id, a);
    return Array.from(map.values()).sort((a, b) =>
      (a.name || '').localeCompare(b.name || ''),
    );
  })();

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSave = async (values: any) => {
    if (auditIds.length === 0) {
      handleClose();
      return;
    }

    // Ambos campos son opcionales: si no se ha rellenado ninguno,
    // cerramos silenciosamente para no hacer PATCHes vacíos.
    const rawAuditorUser = values.auditor_user;
    const auditorTouched = rawAuditorUser !== undefined;
    const dateTouched = values.planning_date !== undefined && values.planning_date !== null;
    if (!auditorTouched && !dateTouched) {
      handleClose();
      return;
    }

    // Sentinel 0 = "Sin asignar" → null; number > 0 → id; undefined → no tocar
    const normalisedAuditorUser =
      typeof rawAuditorUser === 'number' && rawAuditorUser > 0
        ? rawAuditorUser
        : rawAuditorUser === 0
          ? null
          : undefined;

    const normalisedDate = dateTouched
      ? values.planning_date.format('YYYY-MM-DD')
      : undefined;

    // Construcción del payload sólo con los campos que el usuario
    // realmente tocó. Si envía solo auditor, la fecha existente se
    // preserva por cada auditoría (y viceversa).
    const updateData: {
      auditor_user?: number | null;
      planning_date?: string;
    } = {};
    if (auditorTouched) updateData.auditor_user = normalisedAuditorUser as any;
    if (dateTouched) updateData.planning_date = normalisedDate;

    setSaving(true);
    try {
      const { apiClient } = await import('../../../services/api/apiClient');
      const results = await Promise.allSettled(
        auditIds.map((id) => apiClient.patch(`/audits/${id}/`, updateData)),
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const fail = results.length - ok;

      if (fail === 0) {
        message.success(
          t(
            'audit.bulk_assign_success',
            '{{count}} auditorías actualizadas correctamente',
            { count: ok },
          ),
        );
      } else if (ok === 0) {
        message.error(
          t(
            'audit.bulk_assign_all_failed',
            'No se pudo actualizar ninguna auditoría ({{count}} errores)',
            { count: fail },
          ),
        );
      } else {
        message.warning(
          t(
            'audit.bulk_assign_partial',
            '{{ok}} actualizadas, {{fail}} fallaron',
            { ok, fail },
          ),
        );
      }

      form.resetFields();
      onClose();
      onSuccess?.();
    } catch (err: any) {
      message.error(err?.message || t('audit.bulk_assign_error', 'Error en asignación masiva'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t('audit.bulk_assign_auditor', 'Asignar auditor a varias auditorías')}
      open={visible}
      onOk={() => form.submit()}
      onCancel={handleClose}
      confirmLoading={saving}
      okText={t('common.apply', 'Aplicar')}
      cancelText={t('common.cancel', 'Cancelar')}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        message={
          <Space size={4}>
            <Typography.Text>
              {t('audit.bulk_assign_count', 'Se aplicará a')}
            </Typography.Text>
            <Typography.Text strong>{auditIds.length}</Typography.Text>
            <Typography.Text>
              {t('audit.bulk_assign_count_suffix', 'auditorías seleccionadas')}
            </Typography.Text>
          </Space>
        }
        description={t(
          'audit.bulk_assign_hint',
          'Deja en blanco cualquier campo que NO quieras cambiar. Sólo se enviará al servidor lo que rellenes.',
        )}
        style={{ marginBottom: 16 }}
      />
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <Form.Item
          label={t('audit.auditor') || 'Auditor'}
          name="auditor_user"
          extra={t(
            'audit.bulk_auditor_clear_help',
            'Selecciona un auditor para asignarlo a todas. Elige "Sin asignar" para desasignar en masa. Déjalo en blanco para no tocar el auditor actual de cada auditoría.',
          )}
        >
          <Select
            placeholder={t(
              'audit.select_auditor_or_leave_blank',
              'Sin cambios en el auditor',
            )}
            showSearch
            allowClear
            loading={loadingAuditors}
            optionFilterProp="label"
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            dropdownStyle={{ zIndex: 10000 }}
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
            options={[
              {
                value: 0,
                label: (
                  <span style={{ color: '#8c8c8c', fontStyle: 'italic' }}>
                    — {t('planning.unassigned_auditor', 'Sin asignar')} —
                  </span>
                ),
              },
              ...auditorOptions.map((auditor) => ({
                label: (auditor as any).code
                  ? `${auditor.name} (${(auditor as any).code})`
                  : auditor.name,
                value: auditor.id,
              })),
            ]}
          />
        </Form.Item>

        <Form.Item
          label={t('audit.planning_date') || 'Fecha planificada'}
          name="planning_date"
          extra={t(
            'audit.bulk_date_help',
            'Fija la misma fecha en todas las seleccionadas. Déjalo en blanco para no tocar las fechas actuales.',
          )}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            placeholder={t('audit.select_date_or_leave_blank', 'Sin cambios en la fecha')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
